import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  Paper,
  InputAdornment,
  TextField,
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { keyframes } from "@emotion/react";
import { Helmet } from "react-helmet";

import GroupIcon from "../MisBolos/screens/svgviewer-react-output-events";
import StreamIcon from "../MisBolos/screens/svgviewer-react-output-events-stream";
import HeadphonesIcon from "../MisBolos/screens/svgviewer-react-output-headphones";
import Gogicon from './gogicon.jsx'; // Assuming you have a Gogicon component
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const MainContainer = styled(Box)(({ theme }) => ({
  background: "linear-gradient(225deg, #ff4081 0%, #673ab7 100%)",
  color: theme.palette.text.primary,
  minHeight: "100vh",
  paddingBottom: theme.spacing(8),
  fontFamily: "'Inter', sans-serif",
  animation: `${fadeIn} 0.8s ease-out`,
}));

const Hero = styled(Box)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(8, 2),
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 4,
  marginBottom: theme.spacing(8),
  overflow: "hidden",
  position: "relative",
}));

const HeroContent = styled(Box)({
  position: 'relative',
  zIndex: 2,
});

const Section = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(10),
  marginBottom: theme.spacing(10),
}));

const CTAButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  background: theme.palette.success.main,
  color: theme.palette.common.white,
  padding: theme.spacing(1.5, 5),
  fontSize: "1.1rem",
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: "none",
  fontWeight: 700,
  transition: "all 0.3s ease-in-out",
  boxShadow: theme.shadows[3],
  "&:hover": {
    background: theme.palette.success.dark,
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[6],
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  height: "100%",
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[8],
    transform: "translateY(-4px)",
  },
  display: 'flex',
  flexDirection: 'column',
}));

const MisBolosLanding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <MainContainer>
      <Helmet>
        <title>MisBolos | Crea la web de tu banda y conecta con músicos</title>
        <meta
          name="description"
          content="MisBolos te permite crear una página web para tu grupo musical, anunciar conciertos, conectar con artistas y hacerte visible en buscadores."
        />
        <meta
          name="keywords"
          content="música, bandas, conciertos, bolos, músicos, web para músicos, crear página banda"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MusicGroup",
            "name": "MisBolos",
            "url": "https://misbolos.com",
            "description": "Plataforma gratuita para bandas y músicos. Crea tu web y hazte visible.",
            "sameAs": [
              "https://www.instagram.com/misbolos",
              "https://www.facebook.com/misbolos"
            ]
          })}
        </script>
      </Helmet>

      <Hero id="hero">
        <HeroContent id="hero-content">
          <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ animation: `${fadeIn} 1s ease-out` }}
          >
            La web para músicos y bandas emergentes
          </Typography>

          <img
            src="https://bluejims.com:5005/misbolos/image/686307414b7f704168701ad9"
            alt="Banner MisBolos"
            style={{
              width: "100%",
              maxWidth: "500px",
              borderRadius: theme.shape.borderRadius * 2,
              marginBottom: theme.spacing(4),
              maxHeight: isMobile ? "180px" : "280px",
              objectFit: "cover",
              boxShadow: theme.shadows[5],
            }}
          />

          <Typography
            variant={isMobile ? "h5" : "h3"}
            component="h2"
            fontWeight="bold"
            gutterBottom
            sx={{ animation: `${fadeIn} 1.1s ease-out` }}
          >
            Crea gratis una web única para tu grupo
          </Typography>

          <Typography
            variant="h6"
            component="p"
            sx={{ animation: `${fadeIn} 1.2s ease-out` }}
          >
            Músicos, bandas y bolos en tu zona. ¡Hazte visible!
          </Typography>

          <CTAButton variant="contained" sx={{ animation: `${fadeIn} 1.4s ease-out`, background: "#222b63" }}>
            Crear web gratis
          </CTAButton>
        </HeroContent>
      </Hero>

      <Container maxWidth="lg">
        <Section>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            align="center"
            color="white"
            mb={6}
          >
            ¿Qué puedes hacer en MisBolos?
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                icon: <GroupIcon width={64} height={64} />,
                title: "Descubre grupos cercanos y date a conocer",
                text: "Comparte tu enlace exclusivo a la web de tu grupo o tu perfil musical.",
              },
              {
                icon: <StreamIcon width={64} height={64} />,
                title: "Gestión de conciertos",
                text: "Publica tus próximos bolos, haz que la gente sepa dónde tocas.",
              },
              {
                icon: <HeadphonesIcon width={64} height={64} />,
                title: "Conecta con músicos y aparece en búsquedas",
                text: "Descubre otros artistas y aparece en buscadores como Google.",
              }
            ].map(({ icon, title, text }, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <StyledCard sx={{ animation: `${scaleIn} 0.5s ease-out forwards ${0.1 * idx}s` }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {icon}
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {text}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section textAlign="center">
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            align="center"
            color="white"
            mb={4}
          >
            Gratis para músicos
          </Typography>

          <Typography
            variant="h6"
            align="center"
            maxWidth="800px"
            mx="auto"
            mb={4}
            color="white"
          >
            MisBolos nace de la necesidad real de los músicos de tener visibilidad online sin complicaciones.
          </Typography>

          <Box textAlign="center">
            <CTAButton variant="contained" sx={{ background: '#222b63' }}>
              Crear mi página ahora
            </CTAButton>
          </Box>

          <Box
            sx={{
              maxWidth: 800,
              margin: "0 auto",
              marginTop: 4,
              padding: 2,
              backgroundColor: "#fff",
              borderRadius: 4,
              boxShadow: 2,
            }}
          >
            <Paper
              component="form"
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px 12px",
                borderRadius: "999px",
                boxShadow: 1,
                mb: 4,
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                placeholder="Buscar bandas en MisBolos"
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      🔍
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Gogicon style={{ width: 24, height: 24 }} />
                    </InputAdornment>
                  )
                }}
                defaultValue="Against Asgard"
              />
            </Paper>

            {/* Resultado 1 */}
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: "10px" }}>
                https://netofcomputers.com/misbolos/weare/Against%20Asgard
              </Typography>
              <Typography variant="h6" component="div" sx={{ fontSize: "18px", color: "#1a0dab" }}>
                Against Asgard – MisBolos
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px", color: "#4d5156" }}>
                Página oficial del grupo Against Asgard en MisBolos. Consulta sus conciertos y síguelos en directo.
              </Typography>
            </Box>
          </Box>
        </Section>
      </Container>
    </MainContainer>
  );
};

export default MisBolosLanding;
